import { AnalisisLaboratorioResponse } from "../../../../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { fx_createFertilizacionGetYearGroupListByFincaIdService } from "../../../../modulos/fertilizacion/services/FertilizacionGetYearGroupListByFincaIdService";
import { fx_createFertilizacionParseRequestEmpty } from "../../../../modulos/fertilizacion/request/FertilizacionParseRequest";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { fx_createLoteGrpcService } from "../../../../modulos/terceros/services/LoteGrpcService";
import { IGetListYearGroupService } from "../../../../abstractions/services/actions/IGetListYearGroupService";
import { serviceProvider } from "../../../../comun/provider/ServiceProvider";
import { BaseSecondSidebarItemRender } from "../../../base/sidebar/BaseSecondSidebarItemRender";
import { BaseSecondSidebarRender } from "../../../base/sidebar/BaseSecondSidebarRender";
import { FertilizacionDiagnosticoModalRender } from "../modal/FertilizacionDiagnosticoModalRender";
import { FertilizacionNuevoAnalisisModalRender } from "../modal/FertilizacionNuevoAnalisisModalRender";
import { FertilizacionRegistroLotesModalRender } from "../modal/FertilizacionRegistroLotesModalRender";
import { FertilizacionSidebarItemRender } from "../list/FertilizacionSidebarItemRender";
import { FertilizacionDeleteGrpcService } from "../../../../modulos/fertilizacion/services/FertilizacionDeleteGrpcService";
import { formatFechaCorta } from "../../../../utils/fechaUtils";
import { ConfirmModalRender } from "../../../implementations/modal/ConfirmModalRender";

/** Segundo sidebar del módulo de fertilización: análisis de la finca activa
 *  agrupados por año. Al seleccionar un análisis con lotes pendientes (incluida
 *  la auto-selección del primero al entrar) abre el modal de registro de lotes.
 *  El alta (+) abre el modal de nuevo análisis (carga → parse). */
export class FertilizacionSecondSidebarRender extends BaseSecondSidebarRender<AnalisisLaboratorioResponse> {
  /**
   * @param onBack          Acción del botón volver.
   * @param _onAnalisisForMap Notifica el análisis seleccionado (el mapa lo
   *                          colorea).
   */
  constructor(
    onBack: () => void,
    private readonly _onAnalisisForMap: (
      analisis: AnalisisLaboratorioResponse,
    ) => void,
  ) {
    super(onBack);
  }

  protected get title(): string {
    return "FERTILIZACIÓN";
  }

  /** Análisis de la finca activa agrupados por año. */
  protected getYearGroupListService(): IGetListYearGroupService<AnalisisLaboratorioResponse> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    return fx_createFertilizacionGetYearGroupListByFincaIdService(finca.id);
  }

  /** Item de análisis; al seleccionarse dispara el registro de lotes si tiene
   *  pendientes. */
  protected createItem(
    item: AnalisisLaboratorioResponse,
  ): BaseSecondSidebarItemRender {
    return new FertilizacionSidebarItemRender(
      item,
      (analisis) => this._onAnalisisSelected(analisis),
      () => this._getLotes(),
      (analisis) => void this._abrirRegistroLotes(analisis),
      (analisis) => this._confirmDelete(analisis),
      (analisis) => void this._analizar(analisis),
    );
  }

  /** "Analizar Finca": si el análisis ya tiene recomendación generada abre el
   *  diagnóstico; si no, manda al registro de lotes — es su "Guardar
   *  asignaciones" el que dispara la generación en el server (el propio form
   *  explica por qué se llegó ahí). */
  private async _analizar(
    analisis: AnalisisLaboratorioResponse,
  ): Promise<void> {
    if (analisis.recomendacionFecha == null) {
      await this._abrirRegistroLotes(analisis);
      return;
    }
    const lotes = await this._getLotes();
    if (this.disposed) return;
    new FertilizacionDiagnosticoModalRender(analisis, lotes).init();
  }

  /** Confirma y elimina el análisis; al éxito recarga la página. */
  private _confirmDelete(analisis: AnalisisLaboratorioResponse): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    new ConfirmModalRender({
      title: "Eliminar análisis",
      message: `¿Está seguro que desea eliminar el análisis del <strong>${formatFechaCorta(analisis.fechaAnalisis)}</strong> de <strong>${finca.nombreFinca}</strong>? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        await new FertilizacionDeleteGrpcService().deleteAsync(analisis);
        serviceProvider.getLayoutService().reload();
      },
    }).init();
  }

  /** Botón +: abre el modal de nuevo análisis de la finca activa. */
  protected onAdd(): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    new FertilizacionNuevoAnalisisModalRender(
      fx_createFertilizacionParseRequestEmpty(finca.id),
    ).init();
  }

  /** Al seleccionar un análisis: colorea el mapa con ese análisis y, si tiene
   *  lotes sin asignar, abre el modal de registro de lotes. */
  private _onAnalisisSelected(analisis: AnalisisLaboratorioResponse): void {
    this._onAnalisisForMap(analisis);
    if (analisis.pendingLotsCount <= 0) return;
    void this._abrirRegistroLotes(analisis);
  }

  /** Carga los lotes de la finca y abre el modal de registro de lotes. */
  private async _abrirRegistroLotes(
    analisis: AnalisisLaboratorioResponse,
  ): Promise<void> {
    const lotes = await this._getLotes();
    if (this.disposed) return;
    new FertilizacionRegistroLotesModalRender(analisis, lotes).init();
  }

  /** Lotes de la finca activa, cargados una sola vez y compartidos por todos
   *  los items (para pintar nombre/cultivo/área) y por el modal de registro. */
  private _lotesPromise: Promise<ClienteFincaLoteResponse[]> | null = null;

  private _getLotes(): Promise<ClienteFincaLoteResponse[]> {
    if (!this._lotesPromise) {
      const finca = serviceProvider.getFincaSelectedService().getRequired();
      this._lotesPromise =
        fx_createLoteGrpcService(finca.id).getListAsync();
    }
    return this._lotesPromise;
  }
}
